/**
 * Recurring live calls: the shape of a weekly schedule and the pure helpers
 * that read one. The schedules themselves are admin settings
 * (`src/lib/shared/admin-settings.ts`), set at /admin/calls-and-links.
 *
 * Pure: the only clock is the `now` a caller passes in.
 *
 * Aliases: office hours schedule, live call schedule, weekly call, call times
 */

import { fromZonedTime, toZonedTime } from "date-fns-tz";

/** 0 is Sunday, 6 is Saturday, as `Date.getDay()` counts them. */
export type Weekday = 0 | 1 | 2 | 3 | 4 | 5 | 6;

const WEEKDAYS = [0, 1, 2, 3, 4, 5, 6] as const satisfies readonly Weekday[];

export function isWeekday(value: number): value is Weekday {
  return WEEKDAYS.some((day) => day === value);
}

export interface WeeklyCallSchedule {
  /** The day, in `timeZone`. */
  readonly weekday: Weekday;
  /** 0–23, in `timeZone`. */
  readonly hour: number;
  readonly minute: number;
  /** IANA zone the call is held in, so daylight saving moves with it. */
  readonly timeZone: string;
  /** How the schedule names its zone to a coach: "ET". */
  readonly zoneLabel: string;
}

/**
 * True for a place-based IANA zone such as "America/New_York". Abbreviations
 * such as "EST" and "UTC", and the `Etc/` zones such as "Etc/GMT+5", also
 * parse, but they are fixed offsets: a call set in one would move by an hour
 * on the coach's clock when daylight saving starts.
 *
 * The check reads the name as typed. Intl ignores case in zone names, so the
 * `Etc/` check does too. It cannot use the name Intl resolves to: recent ICU
 * resolves "EST" to "America/Panama".
 */
export function isPlaceTimeZone(zone: string): boolean {
  if (!zone.includes("/") || zone.toLowerCase().startsWith("etc/")) {
    return false;
  }
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: zone });
    return true;
  } catch {
    return false;
  }
}

export const WEEKDAY_NAMES = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
] as const satisfies Record<Weekday, string>;

/** "12pm", "9:30am": the way the brief writes a time. */
function clockTime(hour: number, minute: number): string {
  const period = hour < 12 ? "am" : "pm";
  const twelve = hour % 12 === 0 ? 12 : hour % 12;
  const minutes = minute === 0 ? "" : `:${String(minute).padStart(2, "0")}`;
  return `${twelve}${minutes}${period}`;
}

/** "Every Wednesday · 12pm ET". */
export function describeWeeklyCall(schedule: WeeklyCallSchedule): string {
  return `Every ${WEEKDAY_NAMES[schedule.weekday]} · ${clockTime(schedule.hour, schedule.minute)} ${schedule.zoneLabel}`;
}

/**
 * The next time the call starts, strictly after `now`. Worked out on the wall
 * clock of the call's own zone, so a call at noon stays at noon across a
 * daylight-saving change.
 */
export function nextCallAt(schedule: WeeklyCallSchedule, now: Date): Date {
  const wall = toZonedTime(now, schedule.timeZone);
  const daysAhead = (schedule.weekday - wall.getDay() + 7) % 7;
  wall.setDate(wall.getDate() + daysAhead);
  wall.setHours(schedule.hour, schedule.minute, 0, 0);
  const at = fromZonedTime(wall, schedule.timeZone);
  if (at.getTime() > now.getTime()) return at;
  wall.setDate(wall.getDate() + 7);
  return fromZonedTime(wall, schedule.timeZone);
}

/** Weekday and time of `at` on the clock of `timeZone`. */
function wallClockLabel(at: Date, timeZone: string): string {
  const parts = new Intl.DateTimeFormat("en-US", {
    weekday: "long",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
    timeZone,
  }).formatToParts(at);
  const part = (type: Intl.DateTimeFormatPartTypes): string =>
    parts.find((entry) => entry.type === type)?.value ?? "";
  const minute = part("minute");
  const time = `${part("hour")}${minute === "00" ? "" : `:${minute}`}${part("dayPeriod").toLowerCase()}`;
  return `${part("weekday")} ${time}`;
}

/**
 * The next call on the viewer's own clock ("Wednesday 6pm"), or null when her
 * clock reads the same as the call's zone and repeating it would add nothing.
 * The date matters: the gap between two zones changes with daylight saving.
 */
export function localCallTime(
  schedule: WeeklyCallSchedule,
  now: Date,
  viewerTimeZone: string,
): string | null {
  const at = nextCallAt(schedule, now);
  const local = wallClockLabel(at, viewerTimeZone);
  return local === wallClockLabel(at, schedule.timeZone) ? null : local;
}
